import React from "react";
import { Form, Button, Modal, Row, Col } from "react-bootstrap";
import { Formik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import {
  createProject,
  updateProjectData,
} from "../../../../../store/features/project/projectSlice";
import { toast } from "react-toastify";

const ProjectModal = ({
  showForm,
  setShowForm,
  editingProject,
  setEditingProject,
}) => {
  const { assistantSuperAdmin } = useSelector((state) => state.users);
  const { role, _id, name } = useSelector((state) => state.auth);

  const dispatch = useDispatch();

  const initialValues = editingProject
    ? {
        ...editingProject,
        startDate: editingProject.startDate
          ? editingProject.startDate.slice(0, 10)
          : "",
        endDate: editingProject.endDate
          ? editingProject.endDate.slice(0, 10)
          : "",
        cost: editingProject.cost || "",
        budget: editingProject.budget || "",
        notes: editingProject.notes || "",
        addNotes: !!editingProject.notes,
      }
    : {
        projectName: "",
        description: "",
        startDate: "",
        endDate: "",
        assistantSuperAdminId: "",
        document: [],
        cost: "",
        budget: "",
        notes: "",
        addNotes: false,
      };

  const validationSchema = Yup.object().shape({
    projectName: Yup.string().required("Project name is required"),
    description: Yup.string().required("Description is required"),
    startDate: Yup.date().required("Start date is required"),
    endDate: Yup.date()
      .required("End date is required")
      .test(
        "is-same-or-after",
        "End date must be on or after start date",
        function (value) {
          const { startDate } = this.parent;
          if (!startDate || !value) return true;
          return new Date(value) >= new Date(startDate);
        },
      ),
    assistantSuperAdminId: Yup.string().required(
      "Assistant superadmin is required",
    ),
    cost: Yup.number()
      .nullable()
      .typeError("Cost must be a number")
      .min(0, "Cost cannot be negative"),
    budget: Yup.number()
      .nullable()
      .typeError("Budget must be a number")
      .min(0, "Budget cannot be negative")
      .test(
        "budget-not-greater-than-cost",
        "Budget cannot be greater than cost",
        function (value) {
          const { cost } = this.parent;
          if (value == null || cost == null) {
            return true;
          }
          return value <= cost;
        },
      ),
  });

  const handleSubmit = async (values, { resetForm }) => {
    const formData = new FormData();
    values.role = role;

    for (let key in values) {
      if (key === "document" && Array.isArray(values.document)) {
        values.document.forEach((file) => {
          formData.append("document", file);
        });
      } else if (key !== "addNotes") {
        if (key === "notes" && !values.addNotes) {
          continue;
        }
        formData.append(key, values[key]);
      }
    }

    if (editingProject?._id) {
      formData.append("_id", editingProject._id);
    }

    try {
      if (editingProject?._id) {
        await dispatch(updateProjectData(formData)).unwrap();
        toast.success("Project updated!");
      } else {
        await dispatch(createProject(formData)).unwrap();
        toast.success("Project created!");
      }

      resetForm();
      setShowForm(false);
      setEditingProject(null);
    } catch (err) {
      toast.error("Error saving project");
    }
  };

  const asstSuperAdminOptions =
    role === "superadmin" ? assistantSuperAdmin : [{ _id, name }];

  return (
    <Modal size="lg" show={showForm} onHide={() => setShowForm(false)} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {editingProject?.projectName ? "Edit Project" : "Add Project"}
        </Modal.Title>
      </Modal.Header>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          handleChange,
          handleSubmit,
          setFieldValue,
          touched,
          errors,
          isSubmitting,
          validateForm,
          setTouched,
        }) => (
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              validateForm().then((formErrors) => {
                if (Object.keys(formErrors).length > 0) {
                  setTouched(
                    Object.keys(formErrors).reduce((acc, key) => {
                      acc[key] = true;
                      return acc;
                    }, {}),
                  );
                  return;
                }
                handleSubmit();
              });
            }}
          >
            <Modal.Body>
              <Row>
                {/* Project Name */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Project Name <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      name="projectName"
                      value={values.projectName}
                      onChange={handleChange}
                      isInvalid={touched.projectName && errors.projectName}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.projectName}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                {/* Description */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Description <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      name="description"
                      value={values.description}
                      onChange={handleChange}
                      isInvalid={touched.description && errors.description}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.description}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                {/* Start Date */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Start Date <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="date"
                      name="startDate"
                      value={values.startDate}
                      onChange={handleChange}
                      isInvalid={touched.startDate && errors.startDate}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.startDate}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                {/* End Date */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      End Date <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="date"
                      name="endDate"
                      value={values.endDate}
                      onChange={handleChange}
                      isInvalid={touched.endDate && errors.endDate}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.endDate}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                {/* Cost */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Cost</Form.Label>
                    <Form.Control
                      type="number"
                      name="cost"
                      value={values.cost}
                      onChange={handleChange}
                      isInvalid={touched.cost && errors.cost}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.cost}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                {/* Budget */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Budget</Form.Label>
                    <Form.Control
                      type="number"
                      name="budget"
                      value={values.budget}
                      onChange={handleChange}
                      isInvalid={touched.budget && errors.budget}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.budget}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                {/* Assistant Superadmin */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Assistant Superadmin{" "}
                      <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                      name="assistantSuperAdminId"
                      value={values.assistantSuperAdminId}
                      onChange={handleChange}
                      isInvalid={
                        touched.assistantSuperAdminId &&
                        errors.assistantSuperAdminId
                      }
                    >
                      <option value="">Select</option>
                      {asstSuperAdminOptions?.map((pm) => (
                        <option key={pm._id} value={pm._id}>
                          {pm.name}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.assistantSuperAdminId}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                {/* Document Upload */}
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Document</Form.Label>
                    <Form.Control
                      type="file"
                      multiple
                      onChange={(e) => {
                        const files = Array.from(e.currentTarget.files);
                        setFieldValue("document", files);
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Check
                    type="checkbox"
                    label="Add Notes"
                    name="addNotes"
                    checked={values.addNotes}
                    onChange={(e) =>
                      setFieldValue("addNotes", e.target.checked)
                    }
                    className="mb-2"
                  />
                  {values.addNotes && (
                    <Form.Group className="mb-3">
                      <Form.Label>Notes</Form.Label>
                      <Form.Control
                        as="textarea"
                        name="notes"
                        value={values.notes}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  )}
                </Col>
              </Row>
            </Modal.Body>

            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={isSubmitting}>
                {editingProject?.projectName ? "Update" : "Create"}
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default ProjectModal;
